import React, { useCallback, useEffect, useMemo } from 'react';
import { fetchListings as requestListings } from '../../../utils/listingsTransport';
import { Collapse, Input, Switch, InputNumber, Space, Grid } from '@arco-design/web-react';
import {
	t,
	getListing,
	getListingsBlockIds,
	primeListings,
	subscribeListings,
} from '@bay-email/core';
import { listingLayouts } from '@bay-email/core';
import { useFocusIdx, useEditorContext, useEditorProps, TextStyle } from '@bay-email/editor';
import { AttributesPanelWrapper } from '../../attributes/AttributesPanelWrapper';
import {
  NumberField,
  TextField,
  ColorPickerField,
  SwitchField,
  SelectField,
  InputWithUnitField,
} from '../../../../components/Form';
import { useSelectedNode } from '../../../../hooks/useSelectedNode';
import { useExtensionProps } from '../../../../components/Providers/ExtensionProvider';
import { FontFamily } from '../../attributes/FontFamily';
import { Link } from '../../attributes/Link';
import { MergeTags } from '../../attributes/MergeTags';
import { useField } from 'react-final-form';
import { pixelAdapter } from '../../adapter';
import { Padding } from '@extensions/AttributePanel/components/attributes/Padding';
import { resolveListingImages } from '../../../utils/listingImage';
import { BsChevronDown } from 'react-icons/bs';

import { ListingPicker } from './ListingPicker';
import {
  ATTACHED_LISTING_PICKER_EVENT,
  consumePendingListingPickerOpen,
} from '../shared/listingAttachment';
import { ACCOUNT_APP_ID } from '~/lib/environment';

interface ListingItem {
  id: string;
  title?: string;
  address?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  list_price?: number;
  beds?: number;
  baths?: number;
  sq_foot?: number;
  image_data?: string[];
}

interface ActiveView {
  id: 'mylistings' | 'companylistings';
  page: number;
  size: number;
}

interface ListingFiltersPayload {
	startdate?: string;
	enddate?: string;
	propertytype?: string[];
	pricemin?: string;
	pricemax?: string;
	status?: string[];
}

const ALL_PROPERTY_TYPE_VALUES = ['residential', 'multifamily', 'commercial', 'land'];

const SORT_FIELD_MAP: Record<string, string> = {
	list_price: 'list_price',
	adddatetime: 'adddatetime',
};

const normalizePriceFilterValue = (value?: string) => {
	if (!value) return '';
	return value.replace(/[^0-9]/g, '');
};

export interface ListingsPanelProps {
  nodePath?: string;
}

export const Listing: React.FC<ListingsPanelProps> = ({ nodePath }) => {
  const [listings, setListings] = React.useState<ListingItem[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [drawerOpened, setDrawerOpened] = React.useState(false);
  // First open only — a repeat flip is a no-op for the fetch effect but not
  // for setLoading, which left the drawer on its skeleton with nothing to
  // clear it. Also keeps rows already loaded instead of refetching on reopen.
  const hasOpenedRef = React.useRef(false);
  const handleDrawerOpen = React.useCallback(() => {
    if (hasOpenedRef.current) return;
    hasOpenedRef.current = true;
    setDrawerOpened(true);
    setLoading(true);
  }, []);
  const [selectedListings, setSelectedListings] = React.useState<ListingItem[]>([]);
  // Distinct from `drawerOpened` above, which only defers getListings — this
  // one is the edge signal MultipleListingsDrawer watches to actually open.
  const [openSignal, setOpenSignal] = React.useState(0);
  const [activeKeys, setActiveKeys] = React.useState<string[]>(['1', '2', '3', '4']);
  const [activeView, setActiveView] = React.useState<ActiveView>({ id: 'mylistings', page: 1, size: 10 });
  const { selectedNode } = useSelectedNode();
  const { focusIdx } = useFocusIdx();
  const { formHelpers } = useEditorContext();
  const { isDesignAdmin } = useExtensionProps();
  const { mergeTags } = useEditorProps();

  // Get button text field for merge tags
  const { input: buttonTextInput } = useField(`${focusIdx}.attributes.button-text`, {
    parse: v => v,
  });

  const { input: showAddressInput } = useField(`${focusIdx}.attributes.show-address`);
  const { input: showPriceInput } = useField(`${focusIdx}.attributes.show-price`);
  const { input: showDetailsInput } = useField(`${focusIdx}.attributes.show-details`);

  // Initialize show toggles and font sizes to defaults if undefined
  React.useEffect(() => {
    if (focusIdx && formHelpers) {
      const showAddress = formHelpers.getFieldState(`${focusIdx}.attributes.show-address`)?.value;
      const showPrice = formHelpers.getFieldState(`${focusIdx}.attributes.show-price`)?.value;
      const showDetails = formHelpers.getFieldState(`${focusIdx}.attributes.show-details`)?.value;
      const titleFontSize = formHelpers.getFieldState(`${focusIdx}.attributes.title-font-size`)?.value;
      const priceFontSize = formHelpers.getFieldState(`${focusIdx}.attributes.price-font-size`)?.value;
      const addressFontSize = formHelpers.getFieldState(`${focusIdx}.attributes.address-font-size`)?.value;
      const buttonFontSize = formHelpers.getFieldState(`${focusIdx}.attributes.button-font-size`)?.value;

      if (showAddress === undefined) {
        formHelpers.change(`${focusIdx}.attributes.show-address`, true);
      }
      if (showPrice === undefined) {
        formHelpers.change(`${focusIdx}.attributes.show-price`, true);
      }
      if (showDetails === undefined) {
        formHelpers.change(`${focusIdx}.attributes.show-details`, true);
      }
      if (titleFontSize === undefined) {
        formHelpers.change(`${focusIdx}.attributes.title-font-size`, '16px');
      }
      if (priceFontSize === undefined) {
        formHelpers.change(`${focusIdx}.attributes.price-font-size`, '18px');
      }
      if (addressFontSize === undefined) {
        formHelpers.change(`${focusIdx}.attributes.address-font-size`, '14px');
      }
      if (buttonFontSize === undefined) {
        formHelpers.change(`${focusIdx}.attributes.button-font-size`, '14px');
      }
    }
  }, [focusIdx, formHelpers]);

  // Generate layout options from mock data
  const layoutOptions = useMemo(() => {
    return listingLayouts.map((layout: any) => ({
      label: t(layout.name),
      value: layout.id,
    }));
  }, []);

  // Mirror the block's bindings into panel state. Prefer live-resolved copies;
  // fall back to legacy stored objects until the lookups land.
  const boundListingIds = React.useMemo(
    () => getListingsBlockIds(selectedNode?.data?.value),
    [
      selectedNode?.data?.value?.selectedListingIds,
      selectedNode?.data?.value?.selectedListings,
    ],
  );

  const syncFromCache = React.useCallback(() => {
    const resolved = boundListingIds
      .map(id => getListing(id))
      .filter(Boolean) as ListingItem[];

    setSelectedListings(
      resolved.length
        ? resolved
        : selectedNode?.data?.value?.selectedListings || [],
    );
  }, [boundListingIds, selectedNode?.data?.value?.selectedListings]);

  React.useEffect(syncFromCache, [syncFromCache]);

  // Repaint when resolutions land.
  React.useEffect(() => subscribeListings(syncFromCache), [syncFromCache]);

	// Open the drawer when the canvas toolbar's "Select Listing" shortcut fires
	// for this block. The dispatch can land before this panel mounts (focus
	// moves, then the panel mounts after) — consume the sticky request on
	// mount, and also listen live for the already-mounted case.
	useEffect(() => {
		if (consumePendingListingPickerOpen(focusIdx)) {
			setOpenSignal(v => v + 1);
		}
		if (typeof window === 'undefined') return;
		const handleOpen = (event: Event) => {
			const detail = (event as CustomEvent<{ idx?: string }>).detail;
			if (detail?.idx === focusIdx) {
				// Clear the sticky request the dispatch also set, so a later remount of
				// this focusIdx doesn't consume it and reopen the drawer unprompted.
				consumePendingListingPickerOpen(focusIdx);
				setOpenSignal(v => v + 1);
			}
		};
		window.addEventListener(ATTACHED_LISTING_PICKER_EVENT, handleOpen as EventListener);
		return () =>
			window.removeEventListener(ATTACHED_LISTING_PICKER_EVENT, handleOpen as EventListener);
	}, [focusIdx]);

	const getListings = useCallback(async (params: {
		page: number;
		size: number;
		keyword?: string;
		filters?: ListingFiltersPayload;
		sortBy?: string;
		orderDirection?: 'ASC' | 'DESC';
	}) => {
    setLoading(true);
    try {
			const { page, size, keyword, filters, sortBy, orderDirection } = params || { page: 1, size: 10 };
      const reqParams: any = {
        method: 'read',
        CacheData: true,
        Return_Query: false,
        Return_Array: true,
		startpage: page,
        MaxRows: size,
		IsMyTransactions: 'F',
		IsCoordinatorTransactions: 'F',
		ReturnFormat: 'json',
		propertytype: 'residential,multifamily,commercial,land',
		deleteflag: 'n',
		showallproperties: false,
		query: keyword?.trim() ? keyword : '*',
		searchfield: keyword?.trim() ? 'streetaddress,ml_number' : '',
		gridtabid: activeView?.id || 'mylistings',
      };

			if (sortBy && SORT_FIELD_MAP[sortBy]) {
				reqParams.orderby = SORT_FIELD_MAP[sortBy];
				reqParams.orderdirection = (orderDirection || 'DESC').toLowerCase();
			}

			if (filters) {
				reqParams.startdate = filters.startdate ?? '';
				reqParams.enddate = filters.enddate ?? '';
				reqParams.pricemin = normalizePriceFilterValue(filters.pricemin);
				reqParams.pricemax = normalizePriceFilterValue(filters.pricemax);
				reqParams.statuscode = Array.isArray(filters.status) ? filters.status.join(',') : '';

				if (Array.isArray(filters.propertytype)) {
					reqParams.propertytype = filters.propertytype.length
						? filters.propertytype.join(',')
						: ALL_PROPERTY_TYPE_VALUES.join(',');
				}
			}

      const Data = await requestListings({ params: reqParams });
      let results: ListingItem[] = [];
      if (Data?.STATUS === 0 && Array.isArray(Data?.QUERY)) {
        results = Data.QUERY.map((listing: any) => {
          const images = resolveListingImages(listing);
          return {
            // Keep every raw CFC field (zip_code, propertytype, lo_comp, la_name, …) so
            // merge tags whose dictionary translation points at a raw column name —
            // not one of the convenience aliases below — still resolve.
            ...listing,
            id: `${listing?.ml_number}|${listing?.mlsid}|${listing?.statuscode}|${listing?.reduceddatetime}`,
            title: listing?.streetaddress,
            address: `${listing?.streetaddress}`,
            city: listing?.city,
            stateProvince: listing?.state,
            postalCode: listing?.zip_code,
            list_price: listing?.list_price,
            mls: listing?.ml_number,
            dateListed: listing?.listingDisplayDate || listing?.LISTINGDISPLAYDATE || listing?.AddDateTime || listing?.adddatetime,
            image_data: images,
			beds: listing?.beds || 0,
			baths: listing?.baths || 0,
			sq_foot: listing?.sq_foot || 0,
			status: listing?.statuscode,
			propertyType: listing?.propertytype,
			agentName: listing?.agentname || '',
          };
        });
      }
      const totalCount = Data?.TOTALROWCOUNT;
      setListings(results);
      setTotal(totalCount);
      return { listings: results, total: totalCount };
    } catch (e) {
      setListings([]);
      setTotal(0);
      return { listings: [], total: 0 };
    } finally {
      setLoading(false);
    }
  }, [activeView]);

  useEffect(() => {
    if (!drawerOpened) return;
    getListings({...activeView, keyword: ''});
  }, [drawerOpened, getListings]);

  // Update block data when listings are selected
  const onSelect = useCallback(
    (newSelectedListings: ListingItem[]) => {
      setSelectedListings(newSelectedListings);
      // Update the form data using the standard field path pattern
      if (focusIdx) {
        // Persist bindings only; seed the render cache with the values the
        // picker already fetched so the canvas paints without a round trip.
        primeListings(newSelectedListings as any[]);
        formHelpers.change(
          `${focusIdx}.data.value.selectedListingIds`,
          newSelectedListings.map(listing => listing.id).filter(Boolean),
        );
        // Drop any resolved copy a pre-migration save left on the block.
        formHelpers.change(`${focusIdx}.data.value.selectedListings`, undefined);
      }
    },
    [setSelectedListings, formHelpers, focusIdx],
  );

  const handleCollapseAll = React.useCallback(() => {
    if (activeKeys.length > 0) {
      setActiveKeys([]);
    } else {
      setActiveKeys(['1', '2', '3', '4']);
    }
  }, [activeKeys]);

  return (
    <AttributesPanelWrapper onCollapseAll={handleCollapseAll} allCollapsed={activeKeys.length === 0}>
      <Collapse 
		expandIconPosition='right'
		expandIcon={<BsChevronDown size='1rem' />} 
		accordion={false} activeKey={activeKeys} 
		onChange={(key, keys) => setActiveKeys(keys as string[])}
	  >
        <Collapse.Item
          contentStyle={{ padding: '0 0 21px' }}
          name='1'
          header={t('Listing Settings')}
        >
			<ListingPicker
				listings={listings}
				total={total}
				size={10}
				selectedListings={selectedListings}
				loading={loading}
				onSelect={onSelect}
				onPaginationChange={getListings}
				onOpen={handleDrawerOpen}
				disabled={isDesignAdmin}
				setActiveView={setActiveView}
				openSignal={openSignal}
			/>
			<NumberField
				label={t('Max Listings Count')}
				formItem={{ className: 'px-3 mt-2 mb-0' }}
				name={`${focusIdx}.data.value.maxCount`}
				min={1}
				max={24}
			/>
			{/*
				NOTE: Intentionally NO "Sort Listings By" dropdown here.
				That option lives only on the By Criteria block, whose listings
				come from an API query. Multiple Listings uses manually selected
				listings (selectedListings via the picker), so their display order
				is controlled by the user's selection — a sort rule would override
				that hand-picked order. Do not add sorting here.
			*/}
        </Collapse.Item>

        <Collapse.Item
          contentStyle={{ padding: 0 }}
          name='2'
          header={t('Content Block Settings')}
        >
			<div className="p-3">
				<SelectField
					label={t('Layout')}
					name={`${focusIdx}.attributes.layout`}
					className="custom-select-right-caret"
					options={layoutOptions}
				/>

				<Space className="my-2 d-flex justify-content-between align-items-center">
					<span className='fw-bold text-capitalize'>{t('Button Text')}</span>
					{mergeTags && (
					<MergeTags
					    showSearch={true}
						value={buttonTextInput.value}
						onChange={buttonTextInput.onChange}
						children={
							<span className="dynamicFieldDropdown text-secondary cursor-pointer d-flex gap-2">
								<span>{`{ }`}</span>
								<span className="dynamic-field">{t('Dynamic Fields')}</span>
							</span>
						}
						className={`h-100 rounded-end-1 rounded-0 border border-start-0  mergeTag`}
					/>
					)}
				</Space>
				<TextField
					name={`${focusIdx}.attributes.button-text`}
					formItem={{
						labelCol: {
							style: {
								display: 'none',
							},
						},
					}}
				/>
           		<Link isNewDynamicField={true}/>
          </div>
        </Collapse.Item>

		<Collapse.Item
			contentStyle={{ padding: 0 }}
			name='3'
			header={t('Dimension')}
		>
			<div className='d-flex flex-column gap-3 p-3 pt-4'>
				<Padding showResetAll />
			</div>
		</Collapse.Item>

        <Collapse.Item
          contentStyle={{ padding: 0 }}
          name='4'
          header={t('Styling')}
        >
			<div className="d-flex flex-column gap-3 p-3 pb-4">
				{/* Background Color - Full Width */}
				<ColorPickerField
					formItem={{ className: 'm-0' }}
					label={t('Background Color')}
					name={`${focusIdx}.attributes.background-color`}
				/>

				<Grid.Row gutter={16}>
					{/* Listing Name Section */}
					<Grid.Col span={24}>
						<SwitchField
							label={<span style={{ fontWeight: 400, display: 'inline-block' }}>{t('Show Address')}</span>}
							name={`${focusIdx}.attributes.show-address`}
							size="small"
							formItem={{
							className: 'm-0',
							style: {
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								marginBottom: '0px',
							},
							labelCol: {
								span: 0,
								style: {
								order: 2,
								paddingLeft: '12px',
								paddingRight: 0,
								width: 'auto',
								},
							},
							wrapperCol: {
								span: 0,
								style: {
								order: 1,
								},
							},
							}}
						/>
					</Grid.Col>
					{showAddressInput.value && <>
						<Grid.Col span={12} className='mb-3'>
							<ColorPickerField
								formItem={{ className: 'm-0' }}
								label={t('Listing Address Color')}
								name={`${focusIdx}.attributes.listing-name-color`}
							/>
						</Grid.Col>
						<Grid.Col span={12} className='mb-3'>
							<InputWithUnitField
								formItem={{ className: 'm-0' }}
								label={t('Address Font Size (px)')}
								name={`${focusIdx}.attributes.title-font-size`}
								config={pixelAdapter}
							/>
						</Grid.Col>
					</>}
					
					{/* Listing Price Section */}
					<Grid.Col span={24}>
						<SwitchField
							label={<span style={{ fontWeight: 400, display: 'inline-block' }}>{t('Show Price')}</span>}
							name={`${focusIdx}.attributes.show-price`}
							size="small"
							formItem={{
							className: 'm-0',
							style: {
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								marginBottom: '0px',
							},
							labelCol: {
								span: 0,
								style: {
								order: 2,
								paddingLeft: '12px',
								paddingRight: 0,
								width: 'auto',
								},
							},
							wrapperCol: {
								span: 0,
								style: {
								order: 1,
								},
							},
							}}
						/>
					</Grid.Col>
					{showPriceInput.value && <>
						<Grid.Col span={12} className='mb-3'>
							<ColorPickerField
								formItem={{ className: 'm-0' }}
								label={t('Listing Price Color')}
								name={`${focusIdx}.attributes.listing-price-color`}
							/>
						</Grid.Col>
						<Grid.Col span={12} className='mb-3'>
							<InputWithUnitField
								formItem={{ className: 'm-0' }}
								label={t('Price Font Size (px)')}
								name={`${focusIdx}.attributes.price-font-size`}
								config={pixelAdapter}
							/>
						</Grid.Col>
					</>}
					
					{/* Listing Details Section */}
					<Grid.Col span={24}>
						<SwitchField
							label={<span style={{ fontWeight: 400, display: 'inline-block' }}>{t('Show Details')}</span>}
							name={`${focusIdx}.attributes.show-details`}
							size="small"
							formItem={{
							className: 'm-0',
							style: {
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								marginBottom: '0px',
							},
							labelCol: {
								span: 0,
								style: {
								order: 2,
								paddingLeft: '12px',
								paddingRight: 0,
								width: 'auto',
								},
							},
							wrapperCol: {
								span: 0,
								style: {
								order: 1,
								},
							},
							}}
						/>
					</Grid.Col>
					{showDetailsInput.value && <>
						<Grid.Col span={12} className='mb-3'>
							<ColorPickerField
								formItem={{ className: 'm-0' }}
								label={t('Listing Details Color')}
								name={`${focusIdx}.attributes.listing-address-color`}
							/>
						</Grid.Col>
						<Grid.Col span={12} className='mb-3'>
							<InputWithUnitField
								formItem={{ className: 'm-0' }}
								label={t('Details Font Size (px)')}
								name={`${focusIdx}.attributes.address-font-size`}
								config={pixelAdapter}
							/>
						</Grid.Col>
					</>}
					
					{/* Button Section */}
					<Grid.Col span={12} className='mb-3'>
						<ColorPickerField
							formItem={{ className: 'm-0' }}
							label={t('Button Color')}
							name={`${focusIdx}.attributes.button-color`}
						/>
					</Grid.Col>
					<Grid.Col span={12} className='mb-3'>
						<ColorPickerField
							formItem={{ className: 'm-0' }}
							label={t('Button Text Color')}
							name={`${focusIdx}.attributes.button-text-color`}
						/>
					</Grid.Col>
					<Grid.Col span={12}>
						<InputWithUnitField
							formItem={{ className: 'm-0' }}
							label={t('Button Font Size (px)')}
							name={`${focusIdx}.attributes.button-font-size`}
							config={pixelAdapter}
						/>
					</Grid.Col>
				</Grid.Row>
			</div>
        </Collapse.Item>
      </Collapse>
    </AttributesPanelWrapper>
  );
};
